import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Flex,
  IconButton,
  Sheet,
  Text,
  TextField,
} from '@/components/common';
import { DistanceLabel } from '@/components/profile';
import { useCurrentUser } from '@/contexts';
import { analyticsClient } from '@/lib';
import { conversationService, type StampedNote } from '@/services';
import { firstNameOf } from '@/utils';
import './notes.css';

interface NoteReplyDialogProps {
  note: StampedNote | null;
  onClose: () => void;
}

export function NoteReplyDialog({ note: noteProp, onClose }: NoteReplyDialogProps) {
  const { t } = useTranslation('notes');
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // Keeps the last note rendered through the close animation; a new target
  // also resets the draft so replies can't leak between notes.
  const [note, setNote] = useState<StampedNote | null>(noteProp);
  if (noteProp && noteProp.uid !== note?.uid) {
    setNote(noteProp);
    setDraft('');
  }

  const viewProfile = () => {
    if (!note) return;
    analyticsClient.capture('note_opened', {
      target: 'profile',
      source: 'reply',
      author_id: note.uid,
      session_id: analyticsClient.getCurrentSessionId(),
    });
    // The reply target stays in the URL so navigating back reopens the sheet.
    navigate(`/profile/${note.uid}`);
  };

  const send = async (text: string, kind: 'reply' | 'pulse') => {
    if (!note || !user || sending) return;
    setSending(true);
    try {
      const { conversationId } = await conversationService.sendDirectMessage(
        user.uid,
        note.uid,
        text,
        { text: note.text, authorUid: note.uid }
      );
      analyticsClient.capture(kind === 'pulse' ? 'note_pulse_sent' : 'note_reply_sent', {
        conversation_id: conversationId,
        author_id: note.uid,
        session_id: analyticsClient.getCurrentSessionId(),
      });
      setDraft('');
      onClose();
      navigate(`/chat/${conversationId}`);
    } catch (err) {
      console.error('Failed to send note reply:', err);
      toast.error(t('reply.error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet.Root open={Boolean(noteProp)} onOpenChange={open => !open && onClose()}>
      <Sheet.Content>
        <Sheet.Title className="sr-only">
          {t('reply.title', { name: firstNameOf(note?.authorName ?? '') })}
        </Sheet.Title>
        {note && (
          <Flex direction="column" gap="4" px="4" pb="4" pt="1">
            <button
              type="button"
              className="w-full text-left"
              aria-label={t('reply.viewProfileA11y', { name: firstNameOf(note.authorName) })}
              data-testid="note-reply-author"
              onClick={viewProfile}
            >
              <Flex align="center" gap="3">
                <Avatar name={note.authorName} src={note.authorPhotoUrl} size="4" radius="full" />
                <Flex direction="column" gap="0" minWidth="0">
                  <Text weight="bold" truncate wrap="nowrap" className="font-mono">
                    {firstNameOf(note.authorName)}
                  </Text>
                  <Flex align="center" gap="2">
                    {note.availableToday && (
                      <Text size="1" color="gray">
                        {t('reply.live')}
                      </Text>
                    )}
                    <DistanceLabel
                      latitude={note.latitude}
                      longitude={note.longitude}
                      variant="ghost"
                    />
                  </Flex>
                </Flex>
              </Flex>
            </button>
            <Card variant="surface">
              <Text size="2">{note.text}</Text>
            </Card>
            <Flex align="center" gap="2">
              <TextField.Root
                autoFocus
                size="3"
                radius="full"
                className="grow"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && draft.trim()) void send(draft.trim(), 'reply');
                }}
                placeholder={t('reply.placeholder')}
                data-testid="note-reply-input"
              />
              <IconButton
                type="button"
                size="3"
                variant="outline"
                radius="full"
                disabled={sending}
                aria-label={t('reply.pulseA11y')}
                data-testid="note-reply-pulse"
                onClick={() => void send(t('reply.pulseEmoji'), 'pulse')}
              >
                {t('reply.pulseEmoji')}
              </IconButton>
            </Flex>
            <Button
              type="button"
              size="lg"
              fullWidth
              isLoading={sending}
              disabled={!draft.trim()}
              onClick={() => void send(draft.trim(), 'reply')}
              data-testid="note-reply-send"
            >
              {t('reply.send')}
            </Button>
            <Text size="0.5" color="gray" align="center">
              {t('reply.hint')}
            </Text>
          </Flex>
        )}
      </Sheet.Content>
    </Sheet.Root>
  );
}
